<script setup lang="ts">
import { IonContent, IonPage } from '@ionic/vue';
import { useIonRouter } from '@ionic/vue';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import logoUrl from '/assets/logo.png';
import monsterUrl from '/assets/monster.png';
import SpeechBubble from '@/components/SpeechBubble.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';

const router = useIonRouter();
const { t } = useI18n();
const isLoaded = ref(false);
const welcomeVisible = ref(false);

function goToLogin() {
  router.replace('/login');
}

// Improved iOS scrolling function
function resetScrollPosition() {
  // Wait for next render cycle to ensure DOM is ready
  setTimeout(() => {
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    } else {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, 100);
}

onMounted(() => {
  // Reset scroll position when mounting
  resetScrollPosition();
  
  // Fix iOS overscroll behavior - use the correct approach
  // Avoid manipulating body style directly as it can cause issues
  const metaViewport = document.querySelector('meta[name=viewport]');
  if (!metaViewport) {
    // Create viewport meta if it doesn't exist
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, height=device-height';
    document.head.appendChild(meta);
  } else {
    // Update existing viewport meta
    metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, height=device-height');
  }
  
  // Create staggered animation effect
  setTimeout(() => {
    isLoaded.value = true;
  }, 300);
  
  setTimeout(() => {
    welcomeVisible.value = true;
  }, 800);
  
  // Listen for route changes to reset scroll when navigating back to this page
  window.addEventListener('popstate', resetScrollPosition);
  
  // Force iOS to recalculate height properly
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';
    document.body.style.position = 'fixed'; // This forces iOS to recalculate properly
    document.body.style.width = '100%';
    
    // Then remove the fixed position to allow scrolling
    setTimeout(() => {
      document.body.style.position = 'relative';
    }, 300);
  }
});

onBeforeUnmount(() => {
  // Clean up event listeners
  window.removeEventListener('popstate', resetScrollPosition);
});
</script>

<template>
  <ion-page>
    <!-- Make sure scroll is enabled explicitly -->
    <ion-content :scroll-y="true" :fullscreen="true" class="bg-gradient bg-white ion-content-scroll-fix">
      <div class="content-wrapper">
        <div class="container mx-auto">
          <img :src="logoUrl" class="logo-img responsive-logo" :class="{ 'fade-in': isLoaded }" />
          
          <div class="left-section" :class="{ 'slide-in-left': isLoaded }">
            <div class="speech-bubble-container">
              <div class="welcome-text" :class="{ 'fade-in': welcomeVisible }">
                <span class="welcome-greeting">{{ $t('WelcomePage.welcomeGreeting') }}</span>
                <span class="welcome-message">{{ $t('WelcomePage.welcomeMessage') }}</span>
              </div>
              <speech-bubble color="purple" width="264" />
            </div>
            <img class="monster-img" :src="monsterUrl" :class="{ 'bounce-in': welcomeVisible }" />
          </div>
          
          <div class="right-section" :class="{ 'slide-in-right': isLoaded }">
            <img :src="logoUrl" class="desktop-logo" />
            <div class="cta-container">
              <button @click="goToLogin" class="start-button pulse">
                <span>{{ $t('WelcomePage.startButton') }}</span>
                <span class="arrow">→</span>
              </button>
              <p class="subtext">{{ $t('WelcomePage.subtext') }}</p>
            </div>
            <div><LanguageSwitcher/></div>
          </div>
        </div>
      </div>
      
      <div class="decoration-circles">
        <div class="circle circle-1"></div>
        <div class="circle circle-2"></div>
        <div class="circle circle-3"></div>
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
/* Base styles with modern aesthetics */
.bg-gradient {
  --background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

/* Fix for iOS scrolling issues */
.ion-content-scroll-fix {
  --overflow: auto;
  --padding-bottom: 50px; /* Add padding to ensure content is scrollable */
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
}

/* Wrapper to ensure content is tall enough to scroll */
.content-wrapper {
  min-height: 100%;
  width: 100%;
  position: relative;
  padding-bottom: 50px; /* Add padding to ensure scrollability */
}

.container {
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: 24px;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
  position: relative;
  z-index: 1;
  min-height: 100vh; /* Ensure minimum height for scrollability */
}

.left-section,
.right-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Speech bubble styling */
.speech-bubble-container {
  position: relative;
  text-align: center;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.welcome-text {
  position: absolute;
  width: 235px;
  top: 60px;
  left: 16px;
  z-index: 99;
  opacity: 0;
  transition: opacity 0.5s ease, transform 0.5s ease;
  transform: translateY(10px);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.welcome-greeting {
  font-size: 1.8rem;
  font-weight: 600;
  color: #4a148c;
}

.welcome-message {
  font-size: 1.2rem;
  font-weight: 300;
  line-height: 1.4;
  white-space: pre-line; /* This preserves line breaks in the translation */
}

.welcome-text.fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* Monster image styling */
.monster-img {
  position: relative;
  width: 100%;
  max-width: 260px;
  margin-top: -46px;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15));
  transform-origin: bottom center;
  transition: transform 0.8s cubic-bezier(0.18, 0.89, 0.32, 1.28);
  transform: translateY(30px) scale(0.9);
}

.monster-img.bounce-in {
  transform: translateY(0) scale(1);
}

/* Logo styling */
.logo-img {
  width: 100%;
  max-width: 250px;
  margin-bottom: 24px;
  opacity: 0;
  transition: opacity 0.5s ease, transform 0.5s ease;
  transform: translateY(10px);
}

.logo-img.fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* CTA area styling */
.cta-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.start-button {
  font-size: 1.5rem;
  padding: 16px 32px;
  width: 100%;
  max-width: 240px;
  background-color: #7b1fa2;
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 6px 12px rgba(123, 31, 162, 0.3);
}

.start-button:hover {
  background-color: #6a1b9a;
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(123, 31, 162, 0.4);
}

.start-button:active {
  transform: translateY(1px);
  box-shadow: 0 4px 8px rgba(123, 31, 162, 0.3);
}

.arrow {
  transition: transform 0.3s ease;
}

.start-button:hover .arrow {
  transform: translateX(5px);
}

.subtext {
  font-size: 0.95rem;
  color: #666;
  font-weight: 300;
  margin-top: 8px;
}

/* Animation classes */
.slide-in-left {
  animation: slideInLeft 0.8s ease forwards;
}

.slide-in-right {
  animation: slideInRight 0.8s ease forwards;
}

.pulse {
  animation: pulse 2s infinite;
}

/* Decorative elements */
.decoration-circles {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
}

.circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
}

.circle-1 {
  width: 300px;
  height: 300px;
  background-color: #7b1fa2;
  top: -100px;
  right: -50px;
}

.circle-2 {
  width: 200px;
  height: 200px;
  background-color: #ce93d8;
  bottom: 10%;
  left: 5%;
}

.circle-3 {
  width: 150px;
  height: 150px;
  background-color: #ba68c8;
  top: 20%;
  left: 45%;
}

/* Animation keyframes */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(123, 31, 162, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(123, 31, 162, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(123, 31, 162, 0);
  }
}

/* Responsive styling */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
    min-height: 100vh;
  }
  
  .left-section,
  .right-section {
    width: 100%;
  }
  
  .monster-img {
    margin-top: 10px;
    margin-bottom: 20px;
    max-width: 220px;
  }
  
  .logo-img {
    max-width: 180px;
    margin-bottom: 16px;
  }
  
  .start-button {
    font-size: 1.3rem;
    padding: 14px 28px;
  }
  
  .welcome-text {
    top: 50px;
    width: 220px;
  }
  
  .welcome-greeting {
    font-size: 1.6rem;
  }
  
  .welcome-message {
    font-size: 1.1rem;
  }
  
  .responsive-logo {
    display: block;
  }
  
  .desktop-logo {
    display: none;
  }
  
  .circle-1 {
    width: 200px;
    height: 200px;
  }
  
  .circle-2 {
    width: 120px;
    height: 120px;
  }
  
  .circle-3 {
    width: 100px;
    height: 100px;
  }
}

@media (min-width: 769px) {
  .responsive-logo {
    display: none;
  }
  
  .desktop-logo {
    display: block;
    width: 200px;
    margin-bottom: 20px;
  }
}

/* iOS-specific fixes */
@supports (-webkit-touch-callout: none) {
  .ion-content-scroll-fix {
    height: 100%; /* Do not use -webkit-fill-available as it can cause issues */
  }
  
  /* Force content to be taller than viewport to ensure scrolling */
  .content-wrapper {
    min-height: 101vh;
  }
}

/* Firefox specific fixes */
@supports (-moz-appearance: none) {
  .ion-content-scroll-fix {
    scrollbar-width: thin;
  }
}

/* IE specific fixes */
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .container {
    height: auto;

    min-height: 100vh;
  }
}
</style>